import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Container,
  Grid,
  alpha,
} from '@mui/material';
import {
  ChatBubbleOutline as FeedbackIcon,
  Warning as EscalationIcon,
  AutoAwesome as AppreciationIcon,
  Book as SolutionStoryIcon,
  RateReview as PublicTestimonialIcon,
  Group as ProjectIcon,
  Person as IndividualIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { SEARCE_COLORS } from '../../../theme';
import { RequestType, LevelType } from './types';
import { SvgIconProps } from '@mui/material';

interface RequestTypeOption {
  id: RequestType;
  title: string;
  description: string;
  icon: React.ComponentType<SvgIconProps>;
  color: string;
  bgColor: string;
  selectedBg: string;
}

const requestTypes: RequestTypeOption[] = [
  {
    id: 'feedback',
    title: 'Submit Customer Sign-Off',
    description: 'Submit general feedback and suggestions from customers',
    icon: FeedbackIcon,
    color: SEARCE_COLORS.primary,
    bgColor: alpha(SEARCE_COLORS.primary, 0.1),
    selectedBg: alpha(SEARCE_COLORS.primary, 0.08),
  },
  {
    id: 'escalation',
    title: 'Customer Escalation',
    description: 'Report critical issues requiring immediate attention',
    icon: EscalationIcon,
    color: SEARCE_COLORS.warning,
    bgColor: alpha(SEARCE_COLORS.warning, 0.15),
    selectedBg: alpha(SEARCE_COLORS.warning, 0.08),
  },
  {
    id: 'appreciation',
    title: 'Customer Appreciation',
    description: 'Share positive feedback and recognize exceptional service',
    icon: AppreciationIcon,
    color: SEARCE_COLORS.secondary,
    bgColor: alpha(SEARCE_COLORS.secondary, 0.1),
    selectedBg: alpha(SEARCE_COLORS.secondary, 0.08),
  },
  {
    id: 'solution-story',
    title: 'Submit Solution Story',
    description: 'Document and share solution stories and success cases',
    icon: SolutionStoryIcon,
    color: SEARCE_COLORS.success || '#4caf50',
    bgColor: alpha(SEARCE_COLORS.success || '#4caf50', 0.1),
    selectedBg: alpha(SEARCE_COLORS.success || '#4caf50', 0.08),
  },
  {
    id: 'public-testimonial',
    title: 'Submit Public Testimonial',
    description: 'Submit public testimonials and customer success stories',
    icon: PublicTestimonialIcon,
    color: SEARCE_COLORS.info || '#2196f3',
    bgColor: alpha(SEARCE_COLORS.info || '#2196f3', 0.1),
    selectedBg: alpha(SEARCE_COLORS.info || '#2196f3', 0.08),
  },
];

// Page Header Component
interface PageHeaderProps {
  title: string;
  subtitle?: string;
  actionButton?: React.ReactNode;
}

const PageHeader: React.FC<PageHeaderProps> = ({ title, subtitle, actionButton }) => (
  <Box
    sx={{
      backgroundColor: SEARCE_COLORS.white,
      borderBottom: `1px solid ${SEARCE_COLORS.neutral_200}`,
      px: 4,
      py: 1.5,
      position: 'sticky',
      top: 0,
      zIndex: 1200,
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
      mx: { xs: -2, sm: -3, md: -4 },
      mb: 3,
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    }}
  >
    <Box>
      <Typography
        variant="h4"
        component="h1"
        sx={{
          fontWeight: 700,
          color: SEARCE_COLORS.textPrimary,
          fontSize: { xs: '1.3rem', sm: '1.5rem', md: '1.75rem' },
          letterSpacing: '-0.02em',
          lineHeight: 1.2,
          background: `linear-gradient(135deg, ${SEARCE_COLORS.primary} 0%, ${SEARCE_COLORS.secondary} 100%)`,
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text',
          mb: subtitle ? 0.5 : 0,
        }}
      >
        {title}
      </Typography>
      {subtitle && (
        <Typography
          variant="body2"
          component="div"
          sx={{
            color: SEARCE_COLORS.textSecondary,
            fontWeight: 350,
            fontSize: '0.875rem',
          }}
        >
          {subtitle}
        </Typography>
      )}
    </Box>
    {actionButton && <Box>{actionButton}</Box>}
  </Box>
);

interface RequestTypeSelectionPageProps {
  selectedRequestType: RequestType;
  selectedLevel: LevelType;
  onRequestTypeSelect: (type: RequestType) => void;
  onLevelSelect: (level: LevelType) => void;
  onBackToAnalytics: () => void;
  onClearSelection: () => void;
}

export const RequestTypeSelectionPage: React.FC<RequestTypeSelectionPageProps> = ({
  selectedRequestType,
  selectedLevel,
  onRequestTypeSelect,
  onLevelSelect,
  onBackToAnalytics,
  onClearSelection,
}) => {
  return (
    <Box sx={{ px: { xs: 2, sm: 3, md: 4 }, pt: 0, pb: 3 }}>
      {/* Page Header */}
      <PageHeader
        title="New Request"
        subtitle="Select the type of request you want to create and choose the appropriate level"
        actionButton={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {selectedRequestType && (
              <Button
                variant="outlined"
                onClick={onClearSelection}
                sx={{
                  borderColor: SEARCE_COLORS.border,
                  color: SEARCE_COLORS.textSecondary,
                  textTransform: 'none',
                  fontWeight: 500,
                  px: 2,
                  py: 1,
                  borderRadius: 2,
                  '&:hover': {
                    borderColor: SEARCE_COLORS.textSecondary,
                    backgroundColor: alpha(SEARCE_COLORS.textSecondary, 0.04),
                  },
                }}
              >
                Clear Selection
              </Button>
            )}
            <Button
              variant="contained"
              startIcon={<ArrowBackIcon />}
              onClick={onBackToAnalytics}
              sx={{
                backgroundColor: SEARCE_COLORS.primary,
                color: SEARCE_COLORS.white,
                textTransform: 'none',
                fontWeight: 600,
                px: 3,
                py: 1,
                borderRadius: 2,
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                '&:hover': {
                  backgroundColor: SEARCE_COLORS.primaryDark,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                },
              }}
            >
              Back to Analytics
            </Button>
          </Box>
        }
      />

      <Container maxWidth="md" sx={{ mt: 0 }}>
        {/* Content Card */}
        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            border: `1px solid ${SEARCE_COLORS.border}`,
            p: 4,
            backgroundColor: SEARCE_COLORS.cardBackground,
          }}
        >
          {/* Request Type Cards */}
          {/* First Row: Customer Feedback Only */}
          <Grid container spacing={2.5} sx={{ mb: 2 }}>
            {(() => {
              const type = requestTypes[0]; // Customer Feedback
              const Icon = type.icon;
              const isSelected = selectedRequestType === type.id;

              return (
                <Grid item xs={12} key={type.id}>
                  <Paper
                    elevation={0}
                    onClick={() => onRequestTypeSelect(type.id)}
                    sx={{
                      p: 2.5,
                      borderRadius: 2.5,
                      cursor: 'pointer',
                      border: isSelected ? `2px solid ${type.color}` : `1px solid ${SEARCE_COLORS.border}`,
                      backgroundColor: isSelected ? type.selectedBg : SEARCE_COLORS.cardBackground,
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        borderColor: type.color,
                        boxShadow: `0 4px 12px ${alpha(type.color, 0.15)}`,
                      },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                      <Box
                        sx={{
                          width: 56,
                          height: 56,
                          borderRadius: '50%',
                          backgroundColor: type.bgColor,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                        }}
                      >
                        <Icon sx={{ fontSize: 28, color: type.color }} />
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          variant="subtitle1"
                          sx={{
                            fontWeight: 600,
                            color: SEARCE_COLORS.textPrimary,
                            mb: 0.5,
                          }}
                        >
                          {type.title}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: SEARCE_COLORS.textSecondary,
                            lineHeight: 1.5,
                          }}
                        >
                          {type.description}
                        </Typography>
                      </Box>
                    </Box>
                  </Paper>
                </Grid>
              );
            })()}
          </Grid>

          {/* Second Row: Customer Escalation, Customer Appreciation, and Solution Story */}
          <Grid container spacing={2.5} sx={{ mb: 2 }}>
            {requestTypes.slice(1).map((type) => {
              const Icon = type.icon;
              const isSelected = selectedRequestType === type.id;

              return (
                <Grid item xs={12} md={6} key={type.id}>
                  <Paper
                    elevation={0}
                    onClick={() => onRequestTypeSelect(type.id)}
                    sx={{
                      p: 2.5,
                      borderRadius: 2.5,
                      cursor: 'pointer',
                      border: isSelected ? `2px solid ${type.color}` : `1px solid ${SEARCE_COLORS.border}`,
                      backgroundColor: isSelected ? type.selectedBg : SEARCE_COLORS.cardBackground,
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        borderColor: type.color,
                        boxShadow: `0 4px 12px ${alpha(type.color, 0.15)}`,
                      },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                      <Box
                        sx={{
                          width: 56,
                          height: 56,
                          borderRadius: '50%',
                          backgroundColor: type.bgColor,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                        }}
                      >
                        <Icon sx={{ fontSize: 28, color: type.color }} />
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          variant="subtitle1"
                          sx={{
                            fontWeight: 600,
                            color: SEARCE_COLORS.textPrimary,
                            mb: 0.5,
                          }}
                        >
                          {type.title}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: SEARCE_COLORS.textSecondary,
                            lineHeight: 1.5,
                          }}
                        >
                          {type.description}
                        </Typography>
                      </Box>
                    </Box>
                  </Paper>
                </Grid>
              );
            })}
          </Grid>

          {/* Level Selection - Only show for Escalation and Appreciation */}
          {selectedRequestType && selectedRequestType !== 'feedback' && selectedRequestType !== 'solution-story' && selectedRequestType !== 'public-testimonial' && (
            <Box sx={{ mt: 4, pt: 3, borderTop: `1px solid ${SEARCE_COLORS.border}` }}>
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 600,
                  color: SEARCE_COLORS.textPrimary,
                  mb: 2.5,
                }}
              >
                Select Level
              </Typography>
              <Grid container spacing={2.5}>
                {/* Project Level */}
                <Grid item xs={6}>
                  <Paper
                    elevation={0}
                    onClick={() => onLevelSelect('project')}
                    sx={{
                      p: 3,
                      borderRadius: 2.5,
                      cursor: 'pointer',
                      border: `1px solid ${SEARCE_COLORS.border}`,
                      backgroundColor: SEARCE_COLORS.cardBackground,
                      textAlign: 'center',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        borderColor: SEARCE_COLORS.primary,
                        backgroundColor: alpha(SEARCE_COLORS.primary, 0.04),
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: '50%',
                        backgroundColor: alpha(SEARCE_COLORS.primary, 0.1),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mx: 'auto',
                        mb: 1.5,
                      }}
                    >
                      <ProjectIcon sx={{ fontSize: 24, color: SEARCE_COLORS.primary }} />
                    </Box>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: 600,
                        color: SEARCE_COLORS.textPrimary,
                      }}
                    >
                      Project
                    </Typography>
                  </Paper>
                </Grid>

                {/* Individual Level */}
                <Grid item xs={6}>
                  <Paper
                    elevation={0}
                    onClick={() => onLevelSelect('individual')}
                    sx={{
                      p: 3,
                      borderRadius: 2.5,
                      cursor: 'pointer',
                      border: `1px solid ${SEARCE_COLORS.border}`,
                      backgroundColor: SEARCE_COLORS.cardBackground,
                      textAlign: 'center',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        borderColor: SEARCE_COLORS.primary,
                        backgroundColor: alpha(SEARCE_COLORS.primary, 0.04),
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: '50%',
                        backgroundColor: alpha(SEARCE_COLORS.primary, 0.1),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mx: 'auto',
                        mb: 1.5,
                      }}
                    >
                      <IndividualIcon sx={{ fontSize: 24, color: SEARCE_COLORS.primary }} />
                    </Box>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: 600,
                        color: SEARCE_COLORS.textPrimary,
                      }}
                    >
                      Individual
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
            </Box>
          )}
        </Paper>
      </Container>
    </Box>
  );
};
